import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ContextRing } from "@/components/ui/context-ring";
import { menuSurfaceVariants } from "@/components/ui/dropdown-menu";
import { Icons } from "@/components/ui/icons";
import { useSummonedInputMenuStyle } from "@/components/ui/summoned-input-surface-context";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { usePopoverDismiss } from "@/hooks/use-popover-dismiss";
import { prewarmAgentSession } from "@/lib/agent-prewarm";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import type { InputBarFocusedAgent } from "./input-bar-model-pill";
import { InputBarRecentTargets } from "./input-bar-recent-targets";
import type { InputBarConversationController } from "./input-bar-types";
import { useInputPopoverPlacement } from "./use-input-popover-placement";
import { useInputBarContextUsage } from "./use-input-bar-context-usage";

const HOVER_CLOSE_DELAY_MS = 140;
const POPOVER_GAP_PX = 14;

interface Props {
  focusedAgent: InputBarFocusedAgent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationController?: InputBarConversationController;
  /** Sub-agent target: the ring and token line read this worker usage, never Denker session tokens. */
  workerUsage?: { used: number; max: number } | null;
  popoverScope?: "tray";
  density?: "default" | "standalone";
}

export function InputBarConversationContext({
  focusedAgent,
  open,
  onOpenChange,
  conversationController,
  workerUsage,
  popoverScope,
  density = "default",
}: Props) {
  const t = useT(inputBarMessages);
  const summonedInputMenuStyle = useSummonedInputMenuStyle();
  const {
    globalTarget,
    startNewConversation,
    conversationId,
    hasCurrentConversation,
    workerTarget,
    tokenPercent,
    ringPercent,
    menuTitle: contextTitle,
  } = useInputBarContextUsage({
    focusedAgent,
    conversationController,
    workerUsage,
  });
  const standalone = density === "standalone";
  const menuTitle =
    standalone && !workerTarget && !hasCurrentConversation
      ? t("newChatNoContext")
      : contextTitle;
  const requestNewConversation = useInputBarTarget(
    (state) => state.requestNewConversation,
  );
  const clearGlobalTarget = useInputBarTarget((state) => state.clearTarget);
  const [recentRequested, setRecentRequested] = useState(false);
  // Frames (with a controller) keep today's menu; the list loads only on click.
  const showRecent = open && recentRequested && !conversationController;
  useEffect(() => {
    if (!open) setRecentRequested(false);
  }, [open]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current === null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);
  const closeMenu = useCallback(() => onOpenChange(false), [onOpenChange]);
  const scheduleClose = useCallback(() => {
    cancelClose();
    // A click-opened Recent menu closes by outside click or Escape, never mid-pick on pointer leave.
    if (recentRequested && !conversationController) return;
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      if (!popoverRef.current?.contains(document.activeElement)) {
        onOpenChange(false);
      }
    }, HOVER_CLOSE_DELAY_MS);
  }, [cancelClose, onOpenChange, recentRequested, conversationController]);

  useEffect(() => cancelClose, [cancelClose]);
  const openMenu = useCallback(() => {
    cancelClose();
    onOpenChange(true);
  }, [cancelClose, onOpenChange]);
  usePopoverDismiss({
    open,
    onClose: closeMenu,
    anchorRef: buttonRef,
    contentRef: popoverRef,
    stopEscapePropagation: standalone,
  });
  const getPopoverAnchor = useCallback(
    () =>
      standalone
        ? (buttonRef.current?.closest("form") ?? buttonRef.current)
        : buttonRef.current,
    [standalone],
  );
  const popoverStyle = useInputPopoverPlacement(popoverRef, {
    active: open,
    align: "end",
    getAnchor: getPopoverAnchor,
    anchorRef: buttonRef,
    gap: POPOVER_GAP_PX,
    strategy: "fixed",
  });

  const startNewChat = () => {
    if (
      conversationController &&
      !conversationController.canStartNewConversation
    ) {
      onOpenChange(false);
      return;
    }
    if (conversationController) conversationController.onNewConversation();
    else requestNewConversation();
    if (focusedAgent)
      prewarmAgentSession("new_chat", {
        agentId: focusedAgent.id,
        force: true,
        startNewConversation: true,
      });
    onOpenChange(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        title={menuTitle}
        aria-label={t("openChatContext", { title: menuTitle })}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          cancelClose();
          if (!conversationController && !recentRequested) {
            setRecentRequested(true);
            onOpenChange(true);
            return;
          }
          onOpenChange(!open);
        }}
        onPointerEnter={openMenu}
        onPointerLeave={scheduleClose}
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full text-secondary transition-colors hover:bg-[var(--color-interactive-hover)] hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent",
          standalone ? "h-6 w-6" : "h-7 w-7",
        )}
        data-testid={
          standalone ? "standalone-bar-ring" : "input-bar-context-ring"
        }
      >
        <span className="relative flex items-center justify-center">
          {ringPercent !== null || standalone ? (
            <ContextRing
              percent={ringPercent ?? 0}
              size={16}
              data-testid="input-bar-context-ring-progress"
            />
          ) : null}
          {!workerTarget && hasCurrentConversation && tokenPercent === 0 ? (
            <span
              className="pointer-events-none absolute h-1 w-1 rounded-full bg-accent"
              data-testid="input-bar-context-ring-active"
              aria-hidden="true"
            />
          ) : null}
        </span>
      </button>
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              style={{ ...popoverStyle, ...summonedInputMenuStyle }}
              onPointerEnter={cancelClose}
              onPointerLeave={scheduleClose}
              onFocusCapture={cancelClose}
              onKeyDown={(event) => {
                if (event.key !== "Escape") return;
                event.preventDefault();
                event.stopPropagation();
                onOpenChange(false);
                window.requestAnimationFrame(() => buttonRef.current?.focus());
              }}
              className={cn(
                surfaceRoleClassName("menu"),
                menuSurfaceVariants(),
                popoverScope === "tray" ? "z-[100]" : "z-[60]",
                "flex max-w-[calc(100vw-24px)] flex-col gap-2 p-appkit-sm",
                showRecent ? "w-[300px]" : "w-[260px]",
              )}
              data-testid="input-bar-conversation-context"
              data-tray-popover={popoverScope === "tray" || undefined}
              data-interactive="true"
              data-state="open"
              role="menu"
              aria-label={t("chatContext")}
              {...surfaceRoleAttributes("menu", {
                nativeLevel: "root",
                nativeGroup: "menus",
              })}
            >
              <div className="px-appkit-xs pt-appkit-xs">
                <p className="text-appkit-small text-secondary">{menuTitle}</p>
              </div>
              {showRecent ? (
                <InputBarRecentTargets
                  currentConversationId={
                    workerTarget
                      ? (globalTarget?.conversationId ?? null)
                      : hasCurrentConversation
                        ? (conversationId ?? null)
                        : null
                  }
                  onPicked={closeMenu}
                />
              ) : null}
              {!workerTarget && startNewConversation && conversationId ? (
                <button
                  type="button"
                  onClick={() => {
                    if (conversationController) {
                      conversationController.onClear();
                    } else {
                      clearGlobalTarget();
                    }
                    onOpenChange(false);
                  }}
                  className="flex h-8 items-center gap-1 rounded-full px-2 text-appkit-small font-medium text-primary transition-colors hover:bg-[var(--color-interactive-hover)]"
                  data-testid="input-bar-continue-conversation"
                  role="menuitem"
                >
                  <Icons.MessageCircle className="h-3 w-3" aria-hidden="true" />
                  {t("continueCurrentChat")}
                </button>
              ) : null}
              {(!conversationController ||
                conversationController.canStartNewConversation) && (
                <button
                  type="button"
                  onClick={startNewChat}
                  className="flex h-8 items-center gap-1 rounded-full px-2 text-appkit-small font-medium text-accent transition-colors"
                  data-testid="input-bar-new-conversation"
                  role="menuitem"
                >
                  <Icons.Plus className="h-3 w-3" aria-hidden="true" />
                  {t("newChat")}
                </button>
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
